import moment from 'moment-timezone';
import Highcharts from 'highcharts';
import _ from 'lodash';

import {
  getLegendName,
  getSeriesName,
  checkNameForFormulaLegendName,
} from '../utils/chart/ChartManagementFeatures/getChartFeatures';
import { chartWithGroups } from '../utils/chart/ChartManagementFeatures/showChartFeatures';
import { average, median } from '../utils/common';
import { revenueQueryTypes } from '../constants/revenue';
import LineChartTooltip from '../components/tooltip/LineChartTooltip';
import { trendLine, trendLineConfig } from './TrendLine';
import getLineDefaultConfig from './LineChartCommonConfig';
import updateYaxisPlotLines from './ChartPlotline';
import {
  toggleShowAllButton,
  updateChartLabelsButton,
  getSortedSeries,
  updateYAxis,
} from './Common';
import {
  calculateCumulative,
  hideCumulative,
  renderCumulativePaths,
  showCumulative,
} from '../utils/chart/calculateCumulative';
import { sortingOrder, sortingTypes } from '../constants/common';

function redundantZeroFilter(element, index, arr) {
  const getValue = (indexParam) => (arr[indexParam] || [])[1];
  return !(getValue(index) === 0 && getValue(index - 1) === 0 && getValue(index + 1) === 0);
}

const group = (data) => ({
  name: data.map(({ id, name }) => (id !== '' ? `${id}.${name}` : name)).join(' '),
  series: data.flatMap(({ name, series, isSplitBy }) =>
    series.map((s) => {
      return {
        ...s,
        name: getSeriesName(s, name, s.parentId, isSplitBy),
        isSplitBy,
      };
    })
  ),
});

const findSeriesCumulative = (data, name) =>
  data.find(
    ({ id, showCumulative: isShowCumulative }) =>
      (id === name[0] || id[0] === name[0]) && isShowCumulative
  );

const toLineConfig = (
  data,
  options = {},
  showCalcTotal,
  fromDashboard,
  revenueType,
  showSort,
  single,
  triggerLabels,
  unitId
) => {
  const { series } = group(data);

  const seriesSortOrder = sortingOrder.ASCENDING;
  const seriesSortType = sortingTypes.NAME;

  const seriesWithSortedLegend = getSortedSeries(series, seriesSortOrder, seriesSortType);

  const seriesData = seriesWithSortedLegend || series;

  const chartSeries = seriesData.map((s, index) => {
    const values = s.data.map(([, v]) => v);
    const averageValue = average(values);
    const { min, max } = trendLine(values, averageValue);
    const { name, parentId, isSplitBy, color, legendSymbol, legendIndex, dataSum } = s;
    return {
      id: index,
      name: checkNameForFormulaLegendName(name, parentId, isSplitBy, options.legendName),
      data: s.data.filter(redundantZeroFilter),
      trendMin: min,
      trendMax: max,
      average: averageValue,
      isNegative: s.data.some((v) => v[1] < 0),
      type: 'line',
      isSplitBy: s.isSplitBy,
      color,
      marker: {
        symbol: legendSymbol,
      },
      legendIndex,
      dataSum,
    };
  });

  // используется для отрисовки и удаления линии тренда
  let paths;

  let cumulativePaths = [];

  let yMax = 0;

  return {
    ...getLineDefaultConfig(
      chartSeries,
      options,
      fromDashboard,
      data.some(({ showCumulative: isShowCumulative }) => isShowCumulative),
      showSort,
      seriesSortOrder,
      seriesSortType,
      triggerLabels
    ),
    chart: {
      zooming: {
        type: 'x',
      },
      spacingLeft: 0,
      marginLeft: 50,
      events: {
        redraw({ target: chart }) {
          updateYAxis(chart);
          if (!!chart.legend && chart.legend.title !== undefined) {
            chart.legend.title.attr('x', chart.legend.legendWidth / 2);
            chart.legend.title.attr('text-anchor', 'middle');
          }
          // функция убирает кнопку labels в режиме fullscreen графика и показывает кнопку при отключении fullscreen (только на дашбордах)
          updateChartLabelsButton(chart, fromDashboard, triggerLabels);
          if (chart.series) {
            if (chart.showAll !== chart.series.every((s) => s.visible)) {
              toggleShowAllButton(chart);
            }

            // отфильтрованные серии графика, которые отображаются в данный момент
            const chartFilteredSeries = chart.series.filter((s) => s.visible);
            // id отфильтрованных серий
            const ids = chartFilteredSeries.map((s) => s.userOptions.id);
            const plotLines = [];
            // отфильтрованные серии взятые из подготовленного конфига, а не из самих графиков
            const filteredSeries = chartSeries.filter(({ id }) => ids.includes(id));

            // линия тренда
            if (paths) {
              paths.forEach(({ element }) => element.remove());
            }
            if (cumulativePaths) {
              cumulativePaths.forEach(({ element }) => element.remove());
            }
            if (chart.userOptions.trendLine) {
              const xAxis = chart.xAxis[0];
              const yAxis = chart.yAxis[0];
              paths = chartFilteredSeries.map(({ userOptions }, index) =>
                chart.renderer
                  .path([
                    'M',
                    xAxis.toPixels(xAxis.dataMin),
                    yAxis.toPixels(userOptions.trendMin),
                    'L',
                    xAxis.toPixels(xAxis.dataMax),
                    yAxis.toPixels(userOptions.trendMax),
                  ])
                  .attr({
                    ...trendLineConfig,
                    ...{
                      zIndex: 5,
                      stroke: chartFilteredSeries[index].color,
                    },
                  })
                  .add()
              );
            }

            // plotLines
            if (filteredSeries.some((s) => s.isNegative)) {
              plotLines.push({
                id: 'zeroPlotLine',
                lineColor: 'black',
                dashStyle: 'solid',
                text: '',
                textColor: '',
                value: 0,
              });
            }

            if (chart.userOptions.cumulative) {
              const colorIndexes = [];
              const cumulativeLines = chartFilteredSeries
                .filter(({ name }, index) => {
                  const isCumulative = findSeriesCumulative(data, name);
                  if (isCumulative) {
                    colorIndexes.push(index);
                  }
                  return isCumulative;
                })
                .map(({ data: sData }) => {
                  const total = calculateCumulative(sData);
                  yMax = Math.max(yMax, total.at(-1).y);

                  return total;
                });

              if (!chart.userOptions.cumulativeShow) {
                showCumulative(chart, yMax);
              }

              cumulativePaths.forEach(({ element }) => element.remove());

              cumulativePaths = cumulativeLines.map((total, index) =>
                renderCumulativePaths(chart, total, chartFilteredSeries[colorIndexes[index]].color)
              );
            } else if (!chart.userOptions.cumulative && chart.userOptions.cumulativeShow) {
              hideCumulative(chart);
            }

            if (chart.userOptions.averageLine) {
              const averages = filteredSeries.map(({ average: averageItem }) => averageItem);
              const averageValue = _.round(average(averages), 2);

              plotLines.push({
                id: 'averagePlotLine',
                lineColor: 'orange',
                dashStyle: 'dash',
                text: `Avg = ${averageValue}`,
                textColor: 'orange',
                value: averageValue,
              });
            }
            if (chart.userOptions.medianLine) {
              const resultData = filteredSeries.flatMap((d) => d.data.map(([, value]) => value));
              const medianValue = _.round(median(resultData) / filteredSeries.length, 2);

              plotLines.push({
                id: 'medianPlotLine',
                lineColor: 'red',
                dashStyle: 'solid',
                text: `Median = ${medianValue}`,
                textColor: 'red',
                value: medianValue,
              });
            }
            updateYaxisPlotLines(plotLines, chart);
          }
        },
      },
    },
    tooltip: {
      followTouchMove: false,
      followPointer: false,
      useHTML: true,
      formatter(tooltip) {
        const amountOfPoints = data[0]?.categories?.length;
        const {
          chart: { hoverPoint },
        } = tooltip;
        const isPaying = revenueType === revenueQueryTypes.PAYERS;
        return LineChartTooltip(hoverPoint, options, isPaying, amountOfPoints);
      },
    },
    time: {
      getTimezoneOffset(timestamp) {
        return -moment.tz(timestamp, options.timezone).utcOffset();
      },
    },
    xAxis: {
      type: 'datetime',
      categories: null,
      min: null,
      max: null,
      events: {
        afterSetExtremes({ min, max }) {
          Highcharts.charts
            .map((chart) => {
              if (chart?.userOptions.cumulativeShow) {
                const isCurrent = fromDashboard
                  ? this?.chart.userOptions.unitId === chart.userOptions.unitId
                  : this?.chart.series[0]?.name === chart.series[0]?.name;

                if (isCurrent) {
                  chart.yAxis[0].update({ max: Math.max(yMax, chart.yAxis[0].dataMax) });
                }
              }

              return chart;
            })
            .filter((chart) => {
              return (
                chartWithGroups(chart, 'zoomSync', options.group) &&
                (fromDashboard ? chart.userOptions?.unitId === unitId : true)
              );
            })
            .forEach((chart) => {
              chart.xAxis[0].setExtremes(min, max);
            });
        },
      },
    },
    legend: {
      title: {
        text: getLegendName(data, options?.legendName),
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
      },
      maxHeight: 117,
    },
    series: chartSeries,
  };
};

export default toLineConfig;
